import React, { useState, useEffect } from "react";

import { isEmpty } from "lodash";

import SpectrumChart from "../Charts/SpectrumChart";
import HistogramChart from "../Charts/HistogramChart";
import DataTable from "../Tables/DataTable";
import {
    CreateSpectrumPoints,
    CreateHistogramPoints,
} from "../../utils/CreatePoints";
import ReferenceModal from "../Modals/ReferenceModal";
import { Trans, useTranslation } from "react-i18next";
import {
    formatToAbsorbance,
    formatToWaveLength,
} from "../../utils/SpectraDataHelper";
import {
    SPECTRUM_POINTS_BOUNDARY,
    X_AXIS_TYPES,
    Y_AXIS_TYPES,
} from "../../utils/constants";

const ManageData = ({ data, setData, decrementStep, materialsSelected }) => {
    //General States
    const [activeTab, setActiveTab] = useState(0);
    const [materialNames, setMaterialNames] = useState([]);

    //Datatable States
    const [selectedRows, setSelectedRows] = useState([]);
    const [deletedPoints, setDeletedPoints] = useState([]);
    const [dataReference, setDataReference] = useState({
        headers: [],
        rows: [],
    });
    const [modifiedDataReference, setModifiedDataReference] = useState({
        headers: [],
        rows: [],
    });

    //Spectrum States
    const [spectrumPoints, setSpectrumPoints] = useState([]);
    const [selectedSpectrumPoints, setSelectedSpectrumPoints] = useState([]);

    //Histogram States
    const [histogramPoints, setHistogramPoints] = useState([]);
    //Reference Modal States
    const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

    const [isFilterUsed, setIsFilterUsed] = useState(false);
    const [filteredRows, setFilteredRows] = useState([]);
    const [isSpectrumDisabled, setIsSpectrumDisabled] = useState(false);
    const [isWavenumber, setIsWavenumber] = useState(true);
    const [isReflectance, setIsReflectance] = useState(true);
    const [xAxisCheckedReference, setXAxisCheckedReference] = useState({
        value: X_AXIS_TYPES.waveNumber,
    });
    const [yAxisCheckedReference, setYAxisCheckedReference] = useState({
        value: Y_AXIS_TYPES.reflectance,
    });

    const { t } = useTranslation();

    const tabs = [
        { name: t("data") },
        { name: t("spectrum") },
        {
            name: t("histogram"),
            dependsOn: true,
        },
    ];

    useEffect(() => {
        if (data && data.rows) {
            setSpectrumPoints(CreateSpectrumPoints(data));
            if (data.rows?.length <= SPECTRUM_POINTS_BOUNDARY) {
                setIsSpectrumDisabled(false);
            } else {
                setIsSpectrumDisabled(true);
            }
        } else {
            setSpectrumPoints([]);
        }
    }, [data]);

    useEffect(() => {
        if (isFilterUsed && filteredRows?.length <= SPECTRUM_POINTS_BOUNDARY) {
            setIsSpectrumDisabled(false);
        } else if (
            !isFilterUsed &&
            data?.rows?.length > SPECTRUM_POINTS_BOUNDARY
        ) {
            setIsSpectrumDisabled(true);
        }
    }, [filteredRows, isFilterUsed]);

    useEffect(() => {
        if (modifiedDataReference) {
            setHistogramPoints(CreateHistogramPoints(modifiedDataReference));
        } else {
            setHistogramPoints([]);
        }
    }, [modifiedDataReference]);

    useEffect(() => {
        if (!isEmpty(materialsSelected)) {
            let newSelected = {};
            Object.values(materialsSelected).forEach((material) => {
                newSelected[material.materialName] = true;
            });

            setMaterialNames(Object.keys(newSelected).join(", "));
        }
    }, [materialsSelected]);

    useEffect(() => {
        if (!isEmpty(data?.headers)) {
            formatToWaveLength(setData);
        }
    }, [isWavenumber]);

    useEffect(() => {
        if (!isEmpty(data?.rows)) {
            formatToAbsorbance(isReflectance, setData);
        }
    }, [isReflectance]);

    const handleReset = () => {
        const emptyState = {
            headers: [],
            rows: [],
        };
        setSelectedRows([]);
        setDeletedPoints([]);
        setDataReference({ ...emptyState });
        setModifiedDataReference({ ...emptyState });
        setData(null);
        setSpectrumPoints([]);
        setSelectedSpectrumPoints([]);
        setHistogramPoints([]);
        decrementStep();
    };

    const renderTabs = () => {
        return (
            tabs &&
            tabs.map((tab, index) => {
                return (
                    <div
                        key={index}
                        onClick={() => {
                            setActiveTab(index);
                        }}
                        className={`tabs-bnt-item ${
                            activeTab === index ? "active" : ""
                        } ${
                            tab.dependsOn && isEmpty(dataReference.rows)
                                ? "disabled"
                                : ""
                        }`}
                        data-tabs="data-content-wrapper"
                    >
                        {tab.name}
                    </div>
                );
            })
        );
    };

    return (
        <>
            <div className="card-table shadow">
                <div id="customer-content" className="customer-content">
                    <button
                        className="default-transparent-btn mb-5"
                        onClick={handleReset}
                    >
                        <i className="fas fa-caret-left"></i>
                        <span className="ml-2">
                            <Trans i18nKey="goBack">Go Back</Trans>
                        </span>
                    </button>
                    <header className="main-content-header flex items-center justify-between">
                        <div className="flex">
                            <h3>
                                <Trans i18nKey="materialName">
                                    Material Name
                                </Trans>
                                :{" "}
                                <span className="text-sm">{materialNames}</span>
                            </h3>
                        </div>
                        <div className="action-header-wrapper flex items-center">
                            <button
                                className="secondary-green-btn"
                                onClick={() =>
                                    setIsReferenceModalOpen(
                                        !isReferenceModalOpen
                                    )
                                }
                                // disabled={data == null}
                            >
                                <i className="fas fa-file-upload mr-2" />
                                <Trans i18nKey="uploadReferences">
                                    Upload References
                                </Trans>
                            </button>
                        </div>
                    </header>
                </div>
                <div className="tabs-wrapper">
                    <div className="tabs-btn">{renderTabs()}</div>
                    <div className="tabs-content">
                        <DataTable
                            visible={activeTab === 0}
                            data={data}
                            setData={setData}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            selectedSpectrumPoints={selectedSpectrumPoints}
                            setSelectedSpectrumPoints={
                                setSelectedSpectrumPoints
                            }
                            dataReference={dataReference}
                            modifiedDataReference={modifiedDataReference}
                            setModifiedDataReference={setModifiedDataReference}
                            deletedPoints={deletedPoints}
                            setDeletedPoints={setDeletedPoints}
                            setIsFilterUsed={setIsFilterUsed}
                            setFilteredRows={setFilteredRows}
                            isWavenumber={isWavenumber}
                            setIsWavenumber={setIsWavenumber}
                            isReflectance={isReflectance}
                            setIsReflectance={setIsReflectance}
                            xAxisCheckedReference={xAxisCheckedReference}
                            setXAxisCheckedReference={setXAxisCheckedReference}
                            yAxisCheckedReference={yAxisCheckedReference}
                            setYAxisCheckedReference={setYAxisCheckedReference}
                        />

                        <SpectrumChart
                            visible={activeTab === 1}
                            points={spectrumPoints}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            deletedPoints={deletedPoints}
                            setDeletedPoints={setDeletedPoints}
                            isFilterUsed={isFilterUsed}
                            filteredRows={filteredRows}
                            isSpectrumDisabled={isSpectrumDisabled}
                            isWavenumber={isWavenumber}
                            setIsWavenumber={setIsWavenumber}
                            isReflectance={isReflectance}
                            setIsReflectance={setIsReflectance}
                            tooltip={{
                                headerFormat:
                                    "<b>{series.name}</b>: {point.y}<br>",
                                pointFormat: "",
                            }}
                            setXAxisCheckedReference={setXAxisCheckedReference}
                            setYAxisCheckedReference={setYAxisCheckedReference}
                        />

                        <HistogramChart
                            visible={activeTab === 2}
                            points={histogramPoints}
                            isFilterUsed={isFilterUsed}
                            filteredRows={filteredRows}
                        />
                    </div>
                </div>
            </div>
            <ReferenceModal
                isVisible={isReferenceModalOpen}
                setIsVisible={setIsReferenceModalOpen}
                dataReference={dataReference}
                data={data}
                setReferenceData={setDataReference}
                setModifiedDataReference={setModifiedDataReference}
            />
        </>
    );
};

export default ManageData;
